import type {
	StackProps} from "@chakra-ui/react";
import {
	Button,
	Flex,
	HStack, Stack, Text as TextChakra, useMediaQuery,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { colors } from "../../theme/colors";
import TokenField from "./components/TokenField";
import { DataCard } from "../DataCard";
import { Text } from "../Text";
import { minWidth800BP } from "../../constants";

interface ITokenProps {
	isDisabledSend: boolean;
	token: string;
	tokenError: {
		errorStatus: boolean;
		text1: string;
		text2: string;
	};
	cellphone: string;
	time?: number;
	description?: string;
	messageSentToken?: string;
	stackProperties?: StackProps;
	setToken(token: string): void;
	reSendCode(value: string): void;
	sendCode(): void;
}

export const TokenValidation: React.FC<ITokenProps> = ({
	setToken,
	tokenError,
	reSendCode,
	stackProperties,
	cellphone,
	time = 60,
	description = "O código é válido por 3 minutos",
	sendCode,
	isDisabledSend,
	token,
	messageSentToken,
}) => {
	const [isLargerThan800] = useMediaQuery(minWidth800BP);
	const timeToWait = time;
	const timerCounter = useRef(timeToWait);
	const intervalId = useRef<NodeJS.Timer>();

	const [timer, setTimer] = useState(time);
	const [allowResendCode, setAllowResendCode] = useState(true);

	const startTimer = useCallback(() => {
		timerCounter.current = timeToWait;
		setTimer(timerCounter.current);

		intervalId.current = setInterval(() => {
			if (timerCounter.current > 1) {
				timerCounter.current--;
				setTimer(timerCounter.current);
			} else {
				clearInterval(intervalId.current);
				setAllowResendCode(true);
			}
		}, 1000);
		// eslint-disable-next-line
  }, [])

	useEffect(() => {
		startTimer();
		setAllowResendCode(false);
		return () => clearInterval(intervalId.current);
	}, []);

	const handleReSendCode = (cellPhone: string): void => {
		reSendCode(cellPhone);
		startTimer();
		setAllowResendCode(false);
	};

	return (
		<Stack mt={10} {...stackProperties}>
			{!allowResendCode && messageSentToken && (
				<TextChakra
					lineHeight={{ base: "20px", lg: "24px" }}
					fontWeight="normal"
					fontSize={{ base: "14px", lg: "16px" }}
					color={"status.success"}
					textAlign="center"
				>
					{messageSentToken}
				</TextChakra>
			)}
			<DataCard
				textProps={{
					lineHeight: isLargerThan800 ? "24px" : "20px",
					fontSize: isLargerThan800 ? "18px" : "14px",
					color: "gray.600",
					wordBreak: "break-word",
					width: {base: "75%", lg: "100%"},
					alignSelf: "center",
				}}
				description={description}
			>
				<Flex flexDirection="column" alignItems="center" minH={33} minW={33} mb='8px'>
					<HStack gap={1} style={{ margin: "20px 10px 0" }}>
						<TokenField
							isDisabledSend={isDisabledSend}
							sendCode={sendCode}
							tokenError={tokenError}
							setToken={setToken}
							token={token}
						/>
					</HStack>
				</Flex>
			</DataCard>
			<Flex
				width="100%"
				wrap="wrap"
				direction="column"
				justifyContent="center"
				alignItems="center"
				margin="10px 24px"
			>
				{tokenError.errorStatus && tokenError.text1 && (
					<TextChakra
						mt={{ base: "0", lg: "40px" }}
						fontSize={{ base: "14px", lg: "18px" }}
						width='100%'
						textAlign='center'
						fontWeight='400'
						lineHeight='20px'
						color={colors.red[500]}
					>
						{tokenError.text1} &nbsp;
					</TextChakra>
				)}

				{tokenError.errorStatus && tokenError.text2 !== "" && (
					<TextChakra
						fontSize={{ base: "14px", lg: "18px" }}
						width='100%'
						textAlign='center'
						fontWeight='400'
						lineHeight='20px'
						color={colors.red[500]}
					>
						{tokenError.text2}
					</TextChakra>
				)}
			</Flex>
			<Flex
				flexDirection="column"
				width="100%"
				justifyContent="center"
				mb={10}
				minH={126}
			>
				{!allowResendCode && (
					<Text
						preset="preset3"
						fontWeight="regular"
						textAlign="center"
						mt={{ base: "6px", lg: "24px" }}
						fontSize={isLargerThan800 ? "18px" : "14px"}
						lineHeight={isLargerThan800 ? "24px" : "20px"}
						color={"gray.600"}
					>
						Você pode pedir um novo código em&nbsp;
						{`00:${String(timer).padStart(2, "0")}`}
					</Text>
				)}
				<Button
					mb={5}
					mt={17}
					fontSize={isLargerThan800 ? "18px" : "16px"}
					lineHeight={"24px"}
					colorScheme="purple"
					variant="ghost"
					disabled={!allowResendCode}
					onClick={() => handleReSendCode(cellphone)}
				>
					Reenviar código
				</Button>
			</Flex>
		</Stack>
	);
};
